import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverCloseButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, Search2Icon, ViewIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import useDebounce from "../../hooks/useDebounce";
import { CustomToast } from "../../components";
import CustomPagination from "../../components/CustomPagination";
import { CustomExpandTable, Column } from "../../components/CustomExpandTable/customExpandTable";
import FilterCard, { IFilterOptions } from "../../components/FilterCard/filterCard";
import * as routesNames from "../../constant/routes";
import MyDiv from "./customerList.style";
import { inr } from "./customerDetail";

interface CustomerItem {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  outstanding: number;
  status: boolean;
  poc_ID: number | null;
  company_ID: number | null;

}

const sortOptions = [
  "Customer Name (A-Z)",
  "Customer Name (Z-A)",
  "Latest Customers",
  "Oldest Customers",
  "Outstanding (High to Low)",
  "Outstanding (Low to High)",
  "Active Customers First",
  "Inactive Customers First",
];

const customerFilterConfig: IFilterOptions = {
  filterOptions: {
    Status: [
      { id: 1, name: "Active" },
      { id: 2, name: "Inactive" },
    ],
    Outstanding: [
      { id: 1, name: "With Outstanding (> ₹0)" },
      { id: 2, name: "No Outstanding (₹0)" },
    ],
  },
};

export const CustomerList = () => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const navigate = useNavigate();
  const fetchIdRef = useRef(0);
  const scrollCustomerIdRef = useRef<number | null>(null);

  const getSavedState = () => {
    try {
      const saved = sessionStorage.getItem("customer_list_state");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Error reading saved customer list state:", e);
    }
    return null;
  };

  const savedStateRef = useRef<any>(null);
  if (savedStateRef.current === null) {
    savedStateRef.current = getSavedState();
  }
  const savedState = savedStateRef.current;

  const [rawRows, setRawRows] = useState<CustomerItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>(savedState?.search ?? "");
  const debounceSearch = useDebounce(search, 700);

  const [selectedSort, setSelectedSort] = useState<string>(
    savedState?.selectedSort ?? sortOptions[0]
  );
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, number[]>>(
    savedState?.selectedFilters ?? {}
  );

  const [query, setQuery] = useState<{
    lazyParams: {
      first: number;
      rows: number;
      page: number;
      sortField: string;
      sortOrder: number;
    };
  }>(() => {
    if (savedState?.query) {
      return savedState.query;
    }
    return {
      lazyParams: {
        first: 0,
        rows: 10,
        page: 0,
        sortField: "name",
        sortOrder: 1,
      },
    };
  });

  // Restore saved state from sessionStorage once
  useEffect(() => {
    if (!savedState) return;
    if (savedState.selectedCustomerId) {
      scrollCustomerIdRef.current = savedState.selectedCustomerId;
    }
    if (savedState.search !== undefined) {
      setSearch(savedState.search);
    }
    if (savedState.selectedSort !== undefined) {
      setSelectedSort(savedState.selectedSort);
    }
    if (savedState.selectedFilters !== undefined) {
      setSelectedFilters(savedState.selectedFilters);
    }
    sessionStorage.removeItem("customer_list_state");
  }, []);

  // Reset pagination when debounced search term changes
  const previousSearchRef = useRef(debounceSearch);
  useEffect(() => {
    if (previousSearchRef.current === debounceSearch) return;
    previousSearchRef.current = debounceSearch;
    setQuery((prev) => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        first: 0,
        page: 0,
      },
    }));
  }, [debounceSearch]);

  // Load customer data from backend
  const loadCustomers = async () => {
    setLoading(true);
    const fetchId = ++fetchIdRef.current;
    try {
      const queryParams = new URLSearchParams({
        ...(debounceSearch ? { search: debounceSearch } : {}),
        page: String(query.lazyParams.page + 1),
        pageSize: String(query.lazyParams.rows),
      });

      const response = await fetchApi<any>(
        `customers?${queryParams.toString()}`,
        "GET",
        null,
        null,
        ""
      );

      if (fetchId !== fetchIdRef.current) return;

      if (response) {
        setRawRows(response.items || []);
        setTotalCount(response.total ?? (response.items ? response.items.length : 0));
      }
    } catch (error) {
      console.error("Error loading customers:", error);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [query.lazyParams.page, query.lazyParams.rows, debounceSearch]);

  // Filter and sort items
  const processedRows = useMemo(() => {
    let result = [...rawRows];

    // Status filter
    const statusFilter = selectedFilters["Status"];
    if (statusFilter && statusFilter.length === 1) {
      if (statusFilter[0] === 1) {
        result = result.filter((item) => item.status === true);
      } else if (statusFilter[0] === 2) {
        result = result.filter((item) => item.status === false);
      }
    }

    // Outstanding filter
    const outstandingFilter = selectedFilters["Outstanding"];
    if (outstandingFilter && outstandingFilter.length === 1) {
      if (outstandingFilter[0] === 1) {
        result = result.filter((item) => Number(item.outstanding) > 0);
      } else if (outstandingFilter[0] === 2) {
        result = result.filter((item) => Number(item.outstanding) <= 0);
      }
    }

    // Sort
    result.sort((a, b) => {
      switch (selectedSort) {
        case "Customer Name (A-Z)":
          return (a.name || "").localeCompare(b.name || "");
        case "Customer Name (Z-A)":
          return (b.name || "").localeCompare(a.name || "");
        case "Latest Customers":
          return (b.id || 0) - (a.id || 0);
        case "Oldest Customers":
          return (a.id || 0) - (b.id || 0);
        case "Outstanding (High to Low)":
          return (Number(b.outstanding) || 0) - (Number(a.outstanding) || 0);
        case "Outstanding (Low to High)":
          return (Number(a.outstanding) || 0) - (Number(b.outstanding) || 0);
        case "Active Customers First":
          return (b.status ? 1 : 0) - (a.status ? 1 : 0);
        case "Inactive Customers First":
          return (a.status ? 1 : 0) - (b.status ? 1 : 0);
        default:
          return 0;
      }
    });

    return result;
  }, [rawRows, selectedFilters, selectedSort]);

  // Auto-scroll and highlight selected customer row on return from Customer Detail
  useEffect(() => {
    if (!scrollCustomerIdRef.current || processedRows.length === 0) return;
    const customerId = scrollCustomerIdRef.current;
    const exists = processedRows.some((c) => c.id === customerId);
    if (!exists) return;

    setTimeout(() => {
      const element = document.getElementById(`lead-row-${customerId}`);
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.style.outline = "2px solid #2563EB";
      element.style.outlineOffset = "-2px";

      setTimeout(() => {
        element.style.outline = "none";
      }, 2000);

      scrollCustomerIdRef.current = null;
    }, 150);
  }, [processedRows]);

  // Navigation to Customer Detail
  const handleNavigateCustomerDetail = (customer: CustomerItem) => {
    const cleanCustomer: CustomerItem = {
      id: customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      outstanding: customer.outstanding,
      status: customer.status,
      poc_ID: customer.poc_ID,
      company_ID: customer.company_ID,
    };

    const stateToSave = {
      search,
      query,
      selectedSort,
      selectedFilters,
      selectedCustomerId: cleanCustomer.id,
      selectedCompanyId: cleanCustomer.company_ID,
      selectedPocId: cleanCustomer.poc_ID,
    };
    sessionStorage.setItem("customer_list_state", JSON.stringify(stateToSave));

    navigate(`/customers/${cleanCustomer.id}`, {
      state: {
        customer: cleanCustomer,
        from: routesNames.CUSTOMERLIST,
        fromLabel: "Customers",
      },
    });
  };

  // Pagination helpers
  const pageCount = Math.ceil(totalCount / query.lazyParams.rows);

  const handlePageClick = (selectedItem: { selected: number }) => {
    const selected = selectedItem.selected;
    const first = selected * query.lazyParams.rows;

    setQuery((prev) => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        page: selected,
        first,
      },
    }));
  };

  const setRowsPerPage = (rows: number) => {
    setQuery((prev) => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        rows,
        page: 0,
        first: 0,
      },
    }));
  };

  // Count active filter selections
  const activeFilterCount = useMemo(() => {
    return Object.values(selectedFilters).reduce(
      (acc, curr) => acc + (curr ? curr.length : 0),
      0
    );
  }, [selectedFilters]);

  const renderActiveBadge = (isActiveVal?: boolean) => {
    const isActive = Boolean(isActiveVal);
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        bg={isActive ? "#DCFCE7" : "#FEE2E2"}
        color={isActive ? "#15803D" : "#DC2626"}
        px="10px"
        py="4px"
        borderRadius="16px"
        fontSize="12px"
        fontWeight="600"
      >
        <Text m={0}>{isActive ? "Active" : "Inactive"}</Text>
      </Box>
    );
  };

  return (
    <MyDiv>
      {/* Top Header Section */}
      <Box className="top-section">
        <Text className="font-poppins text_xxl text_semibold">Show All Customers</Text>
        <Flex align="center" gap={3} wrap="wrap">
          {/* Search Box */}
          <InputGroup width="260px">
            <InputLeftElement pointerEvents="none">
              <Search2Icon color="gray.500" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="white"
            />
          </InputGroup>

          {/* Filter Popover */}
          <Popover
            placement="bottom-start"
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
          >
            <PopoverTrigger>
              <Button
                rightIcon={<ChevronDownIcon />}
                bg="#F9FAFB"
                border="1px solid #D0D5DD"
                fontSize="14px"
                minW="140px"
                justifyContent="space-between"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
              </Button>
            </PopoverTrigger>
            <FilterCard
              filter={customerFilterConfig}
              selectedFilters={selectedFilters}
              onApply={(filters) => {
                setSelectedFilters(filters);
                setIsFilterOpen(false);
                setQuery((prev) => ({
                  ...prev,
                  lazyParams: {
                    ...prev.lazyParams,
                    page: 0,
                    first: 0,
                  },
                }));
              }}
            />
          </Popover>

          {/* Sort By Popover */}
          <Text fontSize="14px" fontWeight="500" whiteSpace="nowrap">
            Sort By
          </Text>
          <Popover
            placement="bottom-start"
            isOpen={isSortOpen}
            onClose={() => setIsSortOpen(false)}
          >
            <PopoverTrigger>
              <Button
                rightIcon={<ChevronDownIcon />}
                bg="#F9FAFB"
                border="1px solid #D0D5DD"
                fontSize="14px"
                minW="190px"
                justifyContent="space-between"
                onClick={() => setIsSortOpen(!isSortOpen)}
              >
                {selectedSort}
              </Button>
            </PopoverTrigger>
            <PopoverContent w="240px">
              <Box px={4} py={3} borderBottom="1px solid #E5E7EB" position="relative">
                <Text textAlign="center" fontSize="14px" fontWeight="500">
                  Sort By
                </Text>
                <PopoverCloseButton top="8px" right="12px" />
              </Box>
              <Box maxH="240px" overflowY="auto">
                {sortOptions.map((option) => (
                  <Box
                    key={option}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={selectedSort === option ? "#F3F4F6" : "#FFFFFF"}
                    _hover={{ bg: "#F9FAFB" }}
                    onClick={() => {
                      setSelectedSort(option);
                      setIsSortOpen(false);
                    }}
                  >
                    <Text fontSize="14px">{option}</Text>
                  </Box>
                ))}
              </Box>
            </PopoverContent>
          </Popover>
        </Flex>
      </Box>

      {/* Customer Table */}
      <Box
        mt={4}
        overflowX="auto"
        borderRadius="16px"
        sx={{ "&::-webkit-scrollbar": { height: "2px" } }}
      >
        <CustomExpandTable
          loading={loading}
          value={processedRows}
          rowKey="id"
          emptyMessage="No Customers Found"
          onRowClick={(row: any) => handleNavigateCustomerDetail(row)}
          renderExpandedContent={() => <></>}
        >
          {/* 1. S.No */}
          <Column
            header="S.No"
            body={(_, index) => query.lazyParams.first + index + 1}
          />

          {/* 2. Customer Name */}
          <Column
            header="Customer Name"
            field="name"
            sortable={true}
            body={(row: any) => (
              <Tooltip label={row.name || "-"} hasArrow placement="top">
                <Box
                  color="#2563EB"
                  fontWeight="600"
                  maxW="200px"
                  isTruncated
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigateCustomerDetail(row);
                  }}
                >
                  {row.name || "-"}
                </Box>
              </Tooltip>
            )}
          />

          {/* 3. Email */}
          <Column
            header="Email"
            field="email"
            sortable={true}
            body={(row: any) => (
              <Tooltip label={row.email || "-"} hasArrow placement="top">
                <Box maxW="220px" isTruncated color="#475467">
                  {row.email || "-"}
                </Box>
              </Tooltip>
            )}
          />

          {/* 4. Phone */}
          <Column
            header="Phone"
            field="phone"
            sortable={true}
            body={(row: any) => (
              <Text color="#475467" m={0}>
                {row.phone || "-"}
              </Text>
            )}
          />

          {/* 5. Address */}
          <Column
            header="Address"
            field="address"
            body={(row: any) => (
              <Tooltip label={row.address || "-"} hasArrow placement="top">
                <Box maxW="240px" isTruncated color="#475467">
                  {row.address || "-"}
                </Box>
              </Tooltip>
            )}
          />

          {/* 6. Outstanding */}
          <Column
            header="Outstanding"
            field="outstanding"
            sortable={true}
            body={(row: any) => (
              <Text
                fontWeight="600"
                color={Number(row.outstanding) > 0 ? "#D92D20" : "#1D2939"}
                m={0}
              >
                {inr(row.outstanding)}
              </Text>
            )}
          />

          {/* 7. Status */}
          <Column
            header="Status"
            field="status"
            sortable={true}
            body={(row: any) => renderActiveBadge(row.status)}
          />

          {/* 8. Action */}
          <Column
            header="Action"
            body={(row: any) => (
              <Flex align="center" gap={2} wrap="nowrap" width="max-content">
                <Tooltip label="View Customer Details">
                  <Flex
                    align="center"
                    justify="center"
                    w="28px"
                    h="28px"
                    borderRadius="full"
                    bg="purple.50"
                    cursor="pointer"
                    _hover={{ bg: "purple.100" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateCustomerDetail(row);
                    }}
                  >
                    <ViewIcon color="purple.500" boxSize={3.5} />
                  </Flex>
                </Tooltip>
              </Flex>
            )}
          />
        </CustomExpandTable>
      </Box>

      {/* Pagination */}
      {totalCount > 0 && (
        <CustomPagination
          pageCount={pageCount}
          handlePageClick={handlePageClick}
          rowsPerPage={query.lazyParams.rows}
          rowsPerPageOptions={[10, 20, 50, 100]}
          setRowsPerPage={setRowsPerPage}
          totalRecords={totalCount}
          currentPage={query.lazyParams.page}
        />
      )}
    </MyDiv>
  );
};
